import { useEffect, useRef, useCallback } from "react";
import { SocketManager } from "../../../context/Socket/SocketContext";

const COMPOSING_TIMEOUT_MS = 4000;

/**
 * Gestiona la presencia bidireccional del agente humano:
 *  - Suscribe a la presencia del contacto (recepción) al montar.
 *  - Envía "composing" al teclear, "paused" tras 4s de silencio.
 *  - Expone callbacks para notificar inicio/fin de grabación de audio.
 *
 * Solo opera para provider=baileys; el backend filtra silenciosamente
 * cualquier otro proveedor.
 */
const useAgentPresence = ({ ticketId, ticketStatus }) => {
  const companyId = localStorage.getItem("companyId");
  const composingTimerRef = useRef(null);
  const lastPresenceRef = useRef(null);

  const emitPresence = useCallback(
    (presence) => {
      if (!ticketId || !companyId) return;
      if (lastPresenceRef.current === presence) return;
      lastPresenceRef.current = presence;
      const socket = SocketManager.getSocket(companyId);
      socket.emit("agent-presence", { ticketId, presence });
    },
    [ticketId, companyId]
  );

  // Suscribir a presencia del contacto cuando se abre el ticket.
  // Incluye bot/pending/open — solo excluye closed.
  useEffect(() => {
    if (!ticketId || !companyId || ticketStatus === "closed") return;
    const socket = SocketManager.getSocket(companyId);
    socket.emit("subscribe-presence", { ticketId });
  }, [ticketId, companyId, ticketStatus]);

  // Limpiar timer al desmontar
  useEffect(() => {
    return () => {
      if (composingTimerRef.current) clearTimeout(composingTimerRef.current);
    };
  }, []);

  const notifyTyping = useCallback(() => {
    if (ticketStatus !== "open") return;
    emitPresence("composing");
    if (composingTimerRef.current) clearTimeout(composingTimerRef.current);
    composingTimerRef.current = setTimeout(() => {
      lastPresenceRef.current = null;
      emitPresence("paused");
    }, COMPOSING_TIMEOUT_MS);
  }, [emitPresence, ticketStatus]);

  const notifyRecordingStart = useCallback(() => {
    if (composingTimerRef.current) clearTimeout(composingTimerRef.current);
    emitPresence("recording");
  }, [emitPresence]);

  const notifyRecordingStop = useCallback(() => {
    lastPresenceRef.current = null;
    emitPresence("paused");
  }, [emitPresence]);

  // Llamar cuando el input se vacía (mensaje enviado o texto borrado).
  // Cancela el timer pendiente y fuerza "paused" para que el siguiente
  // notifyTyping() no quede bloqueado por la deduplicación.
  const notifyReset = useCallback(() => {
    if (composingTimerRef.current) {
      clearTimeout(composingTimerRef.current);
      composingTimerRef.current = null;
    }
    lastPresenceRef.current = null;
    emitPresence("paused");
  }, [emitPresence]);

  return { notifyTyping, notifyRecordingStart, notifyRecordingStop, notifyReset };
};

export default useAgentPresence;
